// Composer Studio — 작성폼 탭 데이터 소스 위젯 편집 (D4-2)
//
// DataSourceDef(editable list)의 필드별 작성폼 위젯 명세(widget/widgetKey/editable) 편집을
// 양식 탭 전역 패널(DataSourcesEditor)에서 작성폼 탭으로 이관한다(사용자 확정 2026-07-05).
// 정본은 dataSourceDef.fields[].widget/widgetKey/editable 1곳 — writeForm 노드 오버라이드로
// 이중 저장하지 않는다. 아래 헬퍼는 불변 갱신(undo 퍼널 apply와 정합)만 담당한다.

import type {
  DataSourceDef,
  DataSourceFieldMap,
  DataSourceFieldWidget,
} from '@safetyhub/shared';

// 편집형 list 판정은 rc로 일원화(web/admin 공유 — 중복 정의 금지). 여기선 기존 import 경로 유지를 위해 재노출.
export { isEditableListDataSource } from '@safetyhub/report-composer';

/** 한 데이터 소스(di)의 한 필드(fi)에 부분 패치를 적용한 다음 dataSources 배열(불변)을 만든다. */
export function patchDataSourceField(
  dataSources: DataSourceDef[],
  di: number,
  fi: number,
  patch: Partial<DataSourceFieldMap>,
): DataSourceDef[] {
  return dataSources.map((d, idx) =>
    idx === di
      ? { ...d, fields: d.fields.map((f, xi) => (xi === fi ? { ...f, ...patch } : f)) }
      : d,
  );
}

/**
 * 필드 위젯을 바꾼 다음 dataSources 배열을 만든다.
 *
 * 위젯 종속 부가 속성은 그 위젯을 벗어나면 **제거**한다(정본 정합) — 'registered'의 widgetKey,
 * 'signature'의 signerNameField·slotKey, image 계열('image'|'annotated-image')의 displayVariant.
 * 남겨 두면 저장 JSON에 어느 위젯 것인지 모를 배선이
 * 떠다니고, 나중에 그 위젯으로 되돌렸을 때 저작자가 지정한 적 없는 값이 되살아난다
 * (되살아난 slotKey는 특히 나쁘다 — 다른 칸의 서명 주소를 가져오는 셈이 된다).
 */
export function setDataSourceFieldWidget(
  dataSources: DataSourceDef[],
  di: number,
  fi: number,
  widget: DataSourceFieldWidget,
): DataSourceDef[] {
  return patchDataSourceField(dataSources, di, fi, {
    widget,
    ...(widget === 'registered' ? {} : { widgetKey: undefined }),
    ...(widget === 'signature' ? {} : { signerNameField: undefined, slotKey: undefined }),
    ...(widget === 'image' || widget === 'annotated-image' ? {} : { displayVariant: undefined }),
    // 2026-09-07 추기 4 신설 축 — photo의 분석 선언·checkbox의 전파 선언도 위젯 종속 부가 속성.
    // photo의 발견사항 조치사진 역전파 선언(findingWriteBack, 2026-09-14)도 같은 결 — image로 바꾸면
    // 업로드가 없으니 반영할 사진이 생기지 않는다(shared judge 조건 2).
    ...(widget === 'photo' ? {} : { photoAnalysis: undefined, findingWriteBack: undefined }),
    ...(widget === 'checkbox' ? {} : { syncTo: undefined }),
  });
}

/**
 * 필드 위젯을 **미지정**으로 되돌린다(행 그리드 어휘 밖 — 객체 필드를 작성폼 트리의 자식 경로
 * 입력(`riskTypeFlags.<키>` checkbox 13개 등)이 소유하는 형상). 위젯 종속 부가 속성은
 * `setDataSourceFieldWidget`과 같은 이유로 전부 제거한다 — 어느 위젯 것인지 모를 배선을 남기지 않는다.
 * 저장 JSON에는 `widget` 키 자체가 사라진다(undefined는 직렬화에서 빠진다).
 */
export function clearDataSourceFieldWidget(
  dataSources: DataSourceDef[],
  di: number,
  fi: number,
): DataSourceDef[] {
  return patchDataSourceField(dataSources, di, fi, {
    widget: undefined,
    widgetKey: undefined,
    signerNameField: undefined,
    slotKey: undefined,
    displayVariant: undefined,
    photoAnalysis: undefined,
    findingWriteBack: undefined,
    syncTo: undefined,
  });
}
